// client/src/utils/userDataService.js
import axios from "axios";
import { setUser, clearUser } from "../redux/features/userSlice";

const USER_KEY = "user";

const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

// ✅ unwrap both shapes:
// - new: { id, email, balance }
// - old: { user: { id, email, balance } }
const unwrapUser = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  if (obj.user && typeof obj.user === "object") return obj.user;
  return obj;
};

// ✅ accept balance number OR string
const normalizeUser = (uRaw) => {
  const u = unwrapUser(uRaw);
  if (!u || typeof u !== "object") return null;

  const b = u.balance;
  const num = typeof b === "number" ? b : typeof b === "string" ? Number(b) : 0;

  return {
    id:             u.id || u._id || null,
    email:          u.email || "",
    fname:          u.fname || "",
    isAdmin:        !!u.isAdmin,
    block:          u.block === "yes" ? "yes" : "no",
    reseller:       u.reseller === "yes" ? "yes" : "no",
    resellerExpiry: u.resellerExpiry || null,
    resellerSource: u.resellerSource || null,
    picture:        typeof u.picture === "string" ? u.picture : null,
    balance:        Number.isFinite(num) && num >= 0 ? num : 0,
  };
};

const clearAuth = (dispatch) => {
  dispatch(clearUser());
  localStorage.removeItem("token");
  localStorage.removeItem(USER_KEY);
};

const getUserData = async (dispatch, force = false) => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      clearAuth(dispatch);
      return;
    }

    // ✅ Use cache (fast)
    const cachedUserStr = localStorage.getItem(USER_KEY);
    if (!force && cachedUserStr) {
      const cachedObj = safeJsonParse(cachedUserStr);
      const cached = normalizeUser(cachedObj);

      if (cached?.id) {
        dispatch(setUser(cached));
        return;
      }

      // bad cache -> remove
      localStorage.removeItem(USER_KEY);
    }

    const res = await axios.post(
      "/api/user/getUserData",
      {},
      {
        headers: {
          Authorization: "Bearer " + token,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
        timeout: 15000,
      }
    );

    if (res.data?.success) {
      const finalUser = normalizeUser(res.data?.data?.user);
      if (!finalUser?.id) {
        clearAuth(dispatch);
        return;
      }

      dispatch(setUser(finalUser));

      // ✅ store in NEW simple shape (flat user)
      localStorage.setItem(USER_KEY, JSON.stringify(finalUser));
      return;
    }

    clearAuth(dispatch);
  } catch (err) {
    console.log(err?.response?.data || err?.message || err);

    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      clearAuth(dispatch);
      return;
    }

    // network error → keep token but reset redux
    dispatch(clearUser());
  }
};

export default getUserData;
